import * as React from 'react'
import ChatbotWrapper from '../../components/ChatbotWrapper'
import styles from './styles.module.scss'

const LAST_UPDATED = 'August 23, 2026'

const Privacy: React.FC = () => {
  return (
    <ChatbotWrapper
      decorate
      title='Privacy Policy'
      header={
        <>
          <p className='display-6'>
            Draco.Bot is designed to keep your streaming data on your own computer. This policy explains the small amount of information that is sent to Draconem Labs, what is processed locally, and what the draconem.dev website records when you use it.
          </p>
          <p className={styles.updated}>Last updated: {LAST_UPDATED}</p>
        </>
      }
    >
      <div className={styles.policy}>
        <section>
          <h2>Who we are</h2>
          <p>
            Draconem Labs operates <strong>Draco.Bot</strong> and the <strong>draconem.dev</strong> website. In this policy, &quot;Draconem Labs,&quot; &quot;we,&quot; and &quot;us&quot; refer to the operator of those services.
          </p>
        </section>

        <section>
          <h2>The short version</h2>
          <p>
            Draco.Bot is a local desktop application. Most information it uses to run your stream stays on your computer and is not sent to Draconem Labs.
          </p>
          <p>Draconem Labs does not sell personal information and does not use Draco.Bot data to build advertising profiles.</p>
        </section>

        <section>
          <h2>Information Draco.Bot processes locally</h2>
          <p>
            Draco.Bot connects directly to services and applications you choose to use, such as Twitch, Discord, OBS Studio, and VTube Studio. Information needed to provide those integrations is generally processed locally on your computer.
          </p>
          <p>Depending on the features you enable, locally processed information may include:</p>
          <ul>
            <li>Twitch account, chat, viewer, channel, event, and authentication information.</li>
            <li>Discord account identity, server and channel metadata, notifications and message information delivered by the local Discord client, and voice-channel state.</li>
            <li>OBS scenes, sources, filters, media, and other local streaming configuration.</li>
            <li>VTube Studio state and actions.</li>
            <li>Draco.Bot commands, actions, event responses, counters, timers, playlists, settings, and configuration.</li>
            <li>Local application logs used for troubleshooting.</li>
          </ul>
          <p>
            Credentials and tokens used to connect Draco.Bot to third-party services are stored locally by Draco.Bot and are not sent to Draconem Labs. Those credentials may, of course, be sent directly to the applicable third-party service when needed to authenticate or use that service.
          </p>
        </section>

        <section>
          <h2>Discord data</h2>
          <p>
            Discord information used by Draco.Bot is processed locally. Draconem Labs does not receive or centrally store Discord messages, notifications, server lists, channel lists, voice-state information, or Discord OAuth credentials from Draco.Bot.
          </p>
        </section>

        <section>
          <h2>Information Draco.Bot sends to Draconem Labs</h2>
          <p>
            Draco.Bot sends a limited amount of information to draconem.dev so the website can provide the public command directory and so Draco.Bot can check for available releases.
          </p>
          <p>The information sent may include:</p>
          <ul>
            <li>Your connected Twitch broadcaster username.</li>
            <li>The Draco.Bot application name and version.</li>
            <li>Enabled built-in command names, descriptions, and minimum access levels.</li>
            <li>Enabled custom command names and minimum access levels.</li>
            <li>Enabled sound-command names and minimum access levels.</li>
            <li>Enabled counter-command names and minimum access levels.</li>
            <li>For enabled playlist items, artist and song-title metadata only.</li>
            <li>Whether Draco.Bot should include release-candidate versions when checking for updates.</li>
          </ul>
          <p>
            Draco.Bot does not upload the action chains or automation logic behind your custom commands, audio files, playlist files or file paths, Discord data, Twitch chat contents, OBS configuration, VTube Studio data, or a copy of your full Draco.Bot configuration to Draconem Labs.
          </p>
        </section>

        <section>
          <h2>Public Twitch information</h2>
          <p>
            When Draco.Bot submits command-directory information for a Twitch broadcaster, draconem.dev may use that broadcaster username to request publicly available profile and live-stream information from Twitch. This may include information such as the broadcaster&apos;s Twitch ID, login and display name, profile description and image, affiliate or partner status, stream title, game, tags, viewer count, stream start time, thumbnail, and online status.
          </p>
          <p>
            This public Twitch information and the command-directory information submitted by Draco.Bot may be displayed publicly on draconem.dev.
          </p>
        </section>

        <section>
          <h2>Website and server logs</h2>
          <p>
            Like most web servers, draconem.dev automatically creates technical access and error logs when a browser or application connects to it. These logs may include your IP address, the date and time of the request, the requested URL, HTTP method and response status, response size, referring page, and browser or User-Agent information.
          </p>
          <p>
            These logs are used for operation, security, and troubleshooting and are ordinarily retained for approximately two weeks before being rotated out.
          </p>
        </section>

        <section>
          <h2>Website analytics, advertising, and cookies</h2>
          <p>
            Draconem.dev does not currently use third-party analytics or advertising trackers and does not use application cookies to track visitors. Draconem Labs does not use Draco.Bot data for targeted advertising.
          </p>
          <p>
            The website currently loads the Montserrat web font from Google Fonts. As a result, your browser may make a request to Google when loading pages that use that font. Information received by Google as part of that request is governed by Google&apos;s own privacy practices.
          </p>
        </section>

        <section>
          <h2>Hosting and third parties</h2>
          <p>
            Draconem.dev is hosted using third-party infrastructure providers that may process limited technical information as necessary to operate the service.
          </p>
          <p>
            Draco.Bot and draconem.dev also interact with third-party services at your request or as needed to provide features, including Twitch and Discord. Those services process information under their own terms and privacy policies.
          </p>
        </section>

        <section>
          <h2>How long information is kept</h2>
          <ul>
            <li>Information stored only by Draco.Bot remains on your computer until you change or remove it.</li>
            <li>Command-directory information stored by draconem.dev remains until it is replaced, manually removed, or the service is discontinued. It does not currently expire automatically.</li>
            <li>Public Twitch information may be refreshed as needed to keep directory information current.</li>
            <li>Web-server access and error logs are ordinarily retained for approximately two weeks.</li>
          </ul>
        </section>

        <section>
          <h2>Your choices</h2>
          <p>
            You control which Draco.Bot integrations and features you enable. Disconnecting or disabling an integration stops Draco.Bot from using that integration while it is disabled.
          </p>
          <p>
            If you would like command-directory information associated with your Twitch account removed from draconem.dev, contact Draconem Labs through our{' '}
            <a href='https://discord.gg/5s2rhp8qBq' target='_blank' rel='noreferrer'>
              Discord community
            </a>
            .
          </p>
        </section>

        <section>
          <h2>Security</h2>
          <p>
            We take reasonable steps to protect information handled by Draconem Labs, but no networked service can guarantee absolute security. Draco.Bot is intentionally designed to minimize the amount of user data that needs to leave your computer.
          </p>
        </section>

        <section>
          <h2>Changes to this policy</h2>
          <p>
            We may update this policy when Draco.Bot, draconem.dev, or the services they use change. The date at the top of this page will be updated when material changes are made.
          </p>
        </section>

        <section>
          <h2>Contact</h2>
          <p>
            Questions about this policy or requests concerning information stored by Draconem Labs can be sent through the{' '}
            <a href='https://discord.gg/5s2rhp8qBq' target='_blank' rel='noreferrer'>
              Draconem Labs Discord community
            </a>
            .
          </p>
        </section>
      </div>
    </ChatbotWrapper>
  )
}

export default Privacy
